import { useRef, useState, useEffect, useCallback } from 'react';
import './DraggableField.css';

const DraggableField = ({
  type,
  id,
  position,
  onPositionChange,
  onDelete,
  disabled = false,
  pageNumber = 1,
  onDragStart: onDragStartCallback,
  size,
  onSizeChange,
  borderColor,
}) => {
  const fieldRef = useRef(null);
  const [fieldSize, setFieldSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = useState(position);
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const [startResizePosition, setStartResizePosition] = useState({ x: 0, y: 0 });
  
  // Set default size based on field type
  useEffect(() => {
    switch (type) {
      case 'signature':
        setFieldSize({ width: 150, height: 60 });
        break;
      case 'initial':
        setFieldSize({ width: 60, height: 40 });
        break;
      case 'date':
        setFieldSize({ width: 120, height: 40 });
        break;
      case 'text':
        setFieldSize({ width: 200, height: 40 });
        break;
      case 'checkbox':
        setFieldSize({ width: 40, height: 40 });
        break;
      default:
        setFieldSize({ width: 120, height: 40 });
    }
  }, [type]);

  // Update position when props change
  useEffect(() => {
    setCurrentPosition(position);
  }, [position]);

  // Update the DOM element position when currentPosition changes
  useEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.style.left = `${currentPosition.x}px`;
      fieldRef.current.style.top = `${currentPosition.y}px`;
    }
  }, [currentPosition]);

  // Handle mouse down to start dragging
  const handleMouseDown = useCallback((e) => {
    if (disabled) return;
    
    // Prevent default to avoid text selection during drag
    e.preventDefault();
    
    // Calculate the offset from the mouse position to the field's top-left corner
    const rect = fieldRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    
    // Add dragging class for visual feedback
    fieldRef.current.classList.add('dragging');
    
    // Call the parent's onDragStart callback if provided
    if (onDragStartCallback) {
      onDragStartCallback();
    }
  }, [disabled, onDragStartCallback]);

  // Handle mouse move during dragging
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    // Get the parent element (document page) to constrain movement
    const parentElement = fieldRef.current.parentElement;
    const parentRect = parentElement.getBoundingClientRect();
    
    // Calculate new position relative to the parent element
    const newX = e.clientX - parentRect.left - dragOffset.x;
    const newY = e.clientY - parentRect.top - dragOffset.y;
    
    // Constrain position to keep field within parent bounds
    const maxX = parentRect.width - fieldSize.width;
    const maxY = parentRect.height - fieldSize.height;
    
    const constrainedX = Math.max(0, Math.min(newX, maxX));
    const constrainedY = Math.max(0, Math.min(newY, maxY));
    
    // Update the field position
    setCurrentPosition({ x: constrainedX, y: constrainedY });
  }, [isDragging, dragOffset, fieldSize]);

  // Handle resize start
  const handleResizeStart = useCallback((e, direction) => {
    if (disabled) return;
    
    // Prevent default to avoid text selection during resize
    e.preventDefault();
    e.stopPropagation();
    
    // Store the initial size and mouse position
    setStartSize({ ...fieldSize });
    setStartResizePosition({ x: e.clientX, y: e.clientY });
    
    // Set resizing state
    setIsResizing(true);
    setResizeDirection(direction);
    
    // Add resizing class for visual feedback
    fieldRef.current.classList.add('resizing');
  }, [disabled, fieldSize]);

  // Handle mouse move during resizing
  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    
    // Calculate the change in position
    const deltaX = e.clientX - startResizePosition.x;
    const deltaY = e.clientY - startResizePosition.y;
    
    // Calculate new size based on resize direction
    let newWidth = startSize.width;
    let newHeight = startSize.height;
    
    switch (resizeDirection) {
      case 'e': // East (right)
        newWidth = Math.max(50, startSize.width + deltaX);
        break;
      case 's': // South (bottom)
        newHeight = Math.max(30, startSize.height + deltaY);
        break;
      case 'se': // Southeast (bottom-right)
        newWidth = Math.max(50, startSize.width + deltaX);
        newHeight = Math.max(30, startSize.height + deltaY);
        break;
      default:
        break;
    }
    
    // Update the field size
    setFieldSize({ width: newWidth, height: newHeight });
  }, [isResizing, resizeDirection, startResizePosition, startSize]);

  // Handle mouse up to end dragging or resizing
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Remove dragging class
      if (fieldRef.current) {
        fieldRef.current.classList.remove('dragging');
      }
      
      // Call the onPositionChange callback with the new position
      if (onPositionChange) {
        onPositionChange(id, currentPosition, pageNumber);
      }
      
      setIsDragging(false);
    }
    
    if (isResizing) {
      // Remove resizing class
      if (fieldRef.current) {
        fieldRef.current.classList.remove('resizing');
      }
      
      // Call the onSizeChange callback with the new size
      if (onSizeChange) {
        onSizeChange(id, fieldSize);
      }
      
      setIsResizing(false);
      setResizeDirection(null);
    }
  }, [isDragging, isResizing, id, currentPosition, fieldSize, pageNumber, onPositionChange, onSizeChange]);

  // Update the DOM element size when fieldSize changes
  useEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.style.width = `${fieldSize.width}px`;
      fieldRef.current.style.height = `${fieldSize.height}px`;
      
      // Log size changes for debugging
      console.log(`Field ${id} size updated:`, fieldSize);
    }
  }, [fieldSize, id]);

  // Add and remove event listeners for mouse move and mouse up
  useEffect(() => {
    // Add appropriate event listeners based on current state
    if (isDragging) {
      console.log(`Field ${id}: Adding drag event listeners`);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else if (isResizing) {
      console.log(`Field ${id}: Adding resize event listeners`);
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    // Cleanup function to remove all event listeners
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove, handleResizeMove, handleMouseUp, id]);

  const handleDragStart = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    // Call the parent's onDragStart callback if provided
    if (onDragStartCallback) {
      onDragStartCallback();
    }

    // Store the field ID and type in the dataTransfer object
    // Use multiple formats for better browser compatibility
    e.dataTransfer.setData('text/plain', id);
    
    // Store detailed data as JSON
    const dragData = JSON.stringify({
      fieldId: id,
      fieldType: type,
      isNew: false
    });
    
    try {
      e.dataTransfer.setData('application/json', dragData);
    } catch (err) {
      console.error('Error setting JSON data:', err);
      // Fallback for browsers that don't support complex data types
      e.dataTransfer.setData('text/plain', `${id}:${type}:false`);
    }
    
    // Set the drag image (the field itself)
    if (fieldRef.current) {
      // Calculate offset to center the drag image under the cursor
      const rect = fieldRef.current.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      
      // Store the offset in the dataTransfer object
      try {
        e.dataTransfer.setData('offset', JSON.stringify({ x: offsetX, y: offsetY }));
      } catch (err) {
        console.error('Error setting offset data:', err);
        // Fallback for browsers that don't support complex data types
        e.dataTransfer.setData('offsetX', String(offsetX));
        e.dataTransfer.setData('offsetY', String(offsetY));
      }
      
      // Set the drag effect
      e.dataTransfer.effectAllowed = 'move';
      
      // Add a class to indicate dragging
      fieldRef.current.classList.add('dragging');
    }
  };

  const handleDragEnd = (e) => {
    console.log('Drag end event for field:', id);
    
    // Remove the dragging class
    if (fieldRef.current) {
      fieldRef.current.classList.remove('dragging');
    }
    
    // If the drag was unsuccessful (not dropped on a valid target),
    // we might need to reset some state in the parent component
    if (e.dataTransfer.dropEffect === 'none') {
      console.log('Drag was cancelled or unsuccessful');
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent any parent events
    console.log('Delete button clicked for field:', id);
    
    // Remove any event listeners before deleting
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleMouseUp);
    
    // Ensure the field is removed from the DOM immediately
    if (fieldRef.current) {
      fieldRef.current.style.display = 'none';
    }
    
    // Call the onDelete callback with a slight delay to ensure event propagation is complete
    setTimeout(() => {
      if (onDelete) {
        onDelete(id);
      }
    }, 10);
  };

  const getFieldLabel = () => {
    switch (type) {
      case 'signature':
        return 'Signature';
      case 'initial':
        return 'Initial';
      case 'date':
        return 'Date';
      case 'text':
        return 'Text';
      case 'checkbox':
        return 'Checkbox';
      default:
        return 'Field';
    }
  };

  return (
    <div
      ref={fieldRef}
      className={`draggable-field field-${type} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
      style={{
        width: `${fieldSize.width}px`,
        height: `${fieldSize.height}px`,
        left: `${currentPosition.x}px`,
        top: `${currentPosition.y}px`,
        cursor: disabled ? 'default' : 'move',
        position: 'absolute',
        zIndex: (isDragging || isResizing) ? 1000 : 1,
        borderColor: borderColor || undefined,
      }}
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseDown={handleMouseDown}
      data-field-id={id}
      data-field-type={type}
    >
      <div className="field-content">
        <div className="field-label">{getFieldLabel()}</div>
        {type === 'checkbox' && (
          <div className="checkbox-preview">
            <div className="checkbox-box"></div>
          </div>
        )}
      </div>
      
      {!disabled && (
        <>
          <button
            type="button"
            className="field-delete-btn"
            onClick={handleDelete}
            aria-label="Delete field"
          >
            ×
          </button>
          
          {/* Resize handles */}
          <div 
            className="resize-handle resize-e" 
            onMouseDown={(e) => handleResizeStart(e, 'e')}
            title="Resize width"
          />
          <div 
            className="resize-handle resize-s" 
            onMouseDown={(e) => handleResizeStart(e, 's')}
            title="Resize height"
          />
          <div 
            className="resize-handle resize-se" 
            onMouseDown={(e) => handleResizeStart(e, 'se')}
            title="Resize"
          />
        </>
      )}
    </div>
  );
};

export default DraggableField;
